import { useEffect, useState } from 'react'
import { collection, getDocs, query, where, onSnapshot } from 'firebase/firestore'
import { BeatLoader } from 'react-spinners'
import { components } from '../components'
import { useNavigate, useParams } from 'react-router-dom'
import { FaArrowLeft, FaTimes, FaBell } from 'react-icons/fa';
import { getCollection } from '../services';
import { getFunctions, httpsCallable } from "firebase/functions";
import dayjs from 'dayjs';
import { db, functions } from '../configs/firebaseConfig'

interface OrderItem {
    id: string
    name: string
    portion: string
    quantity: number
    price: string
    images: string[]
}

interface Order {
    id: string
    qrId: string
    qrType: string
    qrNumber: string
    createdAt: { seconds: number; nanoseconds: number }
    grandTotal: number
    items: OrderItem[]
    orderStatus: number
    orderNumber: number
    paymentMode: string
    paymentStatus: number
    taxName: string
    taxValue: number
    subTotal: number
    vat: string
    vatEnabled: boolean
    transactionId: string
    restaurantId: string
    currencySymbol?: string | null
    customer?: {
        firstName: string
        lastName: string
    }
    waiter?: {
        id: string
        name: string
    }
}

export const OrderDetails: React.FC = () => {

    const [order, setOrder] = useState<Order | null>(null)
    const [loading, setLoading] = useState<boolean>(false)
    const [isLoading, setIsLoading] = useState(false);
    const [currencySymbol, setCurrencySymbol] = useState<string | null>(null);
    const [isBuzzerDisabled, setIsBuzzerDisabled] = useState(false)

    const navigate = useNavigate()

    const handleBack = () => {
        const token = localStorage.getItem('token');
        if (token) {
            navigate(`/${token}`)
        }
    }

    useEffect(() => {
        const tableData = localStorage.getItem('table')
        const table = tableData ? JSON.parse(tableData) : null
        const tableId = table?.id

        if (!tableId) {
            console.log('No table ID found in localStorage.')
            setOrder(null)
            return
        }

        const ordersRef = collection(db, 'orders')
        const q = query(
            ordersRef,
            where('qrId', '==', tableId),
            where('orderStatus', 'in', [0, 1])
        )

        setLoading(true)

        const stopListening = onSnapshot(q, (snapshot) => {
            if (snapshot.empty) {
                console.log('No active order found for this table.')
                setOrder(null)
            } else {
                const docData = snapshot.docs[0]
                const orderData = { id: docData.id, ...docData.data() } as Order
                // console.log('real-time order:', orderData)
                setOrder(orderData)
            }
            setLoading(false)
        }, (error) => {
            console.error('Error fetching order in real-time:', error)
            setOrder(null)
            setLoading(false)
        })

        return () => stopListening()
    }, [])

    useEffect(() => {
        setIsLoading(true);
        getCollection("configurations")
            .then(
                (data: { id: string; currency?: { symbol?: string } }[]) => {

                    if (data?.length > 0) {
                        const config = data.find((item) => item.currency) || data[0];

                        if (config?.currency?.symbol) {
                            setCurrencySymbol(config.currency.symbol);
                        } else {
                            setCurrencySymbol("");
                        }
                    } else {
                        setCurrencySymbol("");
                    }
                    setIsLoading(false);
                }
            )
            .catch((error) => {
                console.error("Error fetching configurations:", error);
                setCurrencySymbol("");
                setIsLoading(false);
            });
    }, []);


    const handleBuzzerClick = async () => {
        if (isBuzzerDisabled || !order?.waiter?.id) return

        const callToWaiter = httpsCallable(functions, "callToWaiter")

        try {
            setIsBuzzerDisabled(true);

            const result = await callToWaiter({
                waiterId: order.waiter.id
            })

            // console.log("Notification result:", result.data);
        } catch (error) {
            console.log(error)
            if (error instanceof Error) {
                console.error("Failed to send notification:", error.message)
            } else {
                console.error("Failed to send notification:", error)
            }
        } finally {
            setIsBuzzerDisabled(false)
        }
    }

    const renderHeader = (): JSX.Element => {
        return (
            <components.Header
                showBurger={true}
                showButton={true}
                headerStyle={{
                    backgroundColor: 'transparent',
                }}
            />
        )
    }

    const renderContent = (): JSX.Element => {
        return (
            <main style={{ backgroundColor: '#f2f4f4', display: 'flex', justifyContent: 'center' }}>
                <div
                    style={{
                        padding: '20px',
                        fontFamily: 'Poppins',
                        maxWidth: '450px',
                        width: '100%',
                    }}
                >

                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '10px 0px',
                            position: 'relative',
                        }}
                    >
                        <button
                            onClick={handleBack}
                            style={{
                                background: 'none',
                                border: 'none',
                                fontSize: '20px',
                                color: '#000',
                                display: 'flex',
                                alignItems: 'center',
                                fontFamily: 'Poppins',
                            }}
                        >
                            <FaArrowLeft size={16} style={{ marginTop: 3 }} />
                            <span style={{ fontSize: "15px", marginLeft: 5 }}>Back</span>
                        </button>


                        <h2
                            style={{
                                margin: 0,
                                fontSize: '1.2rem',
                                fontWeight: '600',
                                color: '#000',
                                fontFamily: 'Poppins',
                            }}
                        >
                            Order Details
                        </h2>

                        <button
                            onClick={handleBuzzerClick}
                            disabled={isBuzzerDisabled}
                            style={{
                                background: '#fdd835',
                                fontFamily: 'Poppins',
                                border: 'none',
                                borderRadius: 20,
                                padding: '5px 10px',
                                fontSize: 14,
                                cursor: isBuzzerDisabled ? 'not-allowed' : 'pointer',
                                opacity: isBuzzerDisabled ? 0.5 : 1,
                                display: 'inline-flex',
                                alignItems: 'center',
                            }}
                        >
                            <FaBell style={{ marginRight: 8 }} /> Call waiter
                        </button>
                    </div>


                    {loading ? (
                        <div style={{ textAlign: 'center', padding: '2rem' }}>
                            <BeatLoader />
                        </div>
                    ) : order ? (
                        <>
                            <div
                                style={{
                                    background: '#fff',
                                    padding: '15px',
                                    borderRadius: '10px',
                                    marginBottom: '20px',
                                    boxShadow: '0 3px 8px rgba(0, 0, 0, 0.1)',
                                }}
                            >
                                <div style={{ marginBottom: '5px' }}>
                                    <strong style={{ fontSize: '14px', color: '#000' }}>Order ID:</strong>{' '}
                                    <span style={{ fontSize: '14px', color: '#000' }}>
                                        {order?.orderNumber || order?.transactionId}
                                    </span>
                                </div>

                                <div style={{ marginBottom: '5px' }}>
                                    <strong style={{ fontSize: '14px', color: '#000' }}>Order Status:</strong>{' '}
                                    <span style={{ fontSize: '14px', color: '#000' }}>
                                        {order.orderStatus === 0
                                            ? 'Pending'
                                            : order.orderStatus === 1
                                                ? 'Accepted'
                                                : order.orderStatus === 2
                                                    ? 'Delivered'
                                                    : order.orderStatus === 3
                                                        ? 'Cancelled'
                                                        : order.orderStatus === 4
                                                            ? 'Cleared'
                                                            : 'Unknown'}
                                    </span>
                                </div>
                                {order.qrType && order.qrNumber && (
                                    <div style={{ marginBottom: '5px' }}>
                                        <strong style={{ fontSize: '14px', color: '#000' }}>Assigned:</strong>{' '}
                                        <span style={{ fontSize: '14px', color: '#000' }}>{order.qrType} - {order.qrNumber}</span>
                                    </div>
                                )}
                                {order.customer && (
                                    <div style={{ marginBottom: '5px' }}>
                                        <strong style={{ fontSize: '14px', color: '#000' }}>Customer:</strong>{' '}
                                        <span style={{ fontSize: '14px', color: '#000' }}>{order.customer.firstName} {order.customer.lastName}</span>
                                    </div>
                                )}
                                {order.waiter && (
                                    <div style={{ marginBottom: '5px' }}>
                                        <strong style={{ fontSize: '14px', color: '#000' }}>Waiter:</strong>{' '}
                                        <span style={{ fontSize: '14px', color: '#000' }}>{order.waiter.name}</span>
                                    </div>
                                )}
                                <div style={{ padding: '10px', borderRadius: '8px', backgroundColor: '#f9f9f9', marginTop: '10px' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                        <thead>
                                            <tr style={{ backgroundColor: '#e8e8e8', textAlign: 'left' }}>
                                                <th style={{ padding: '8px', fontSize: '14px', fontWeight: 'bold', color: '#000' }}>Item</th>
                                                <th style={{ padding: '8px', fontSize: '14px', fontWeight: 'bold', color: '#000' }}>Price</th>
                                                <th style={{ padding: '8px', fontSize: '14px', fontWeight: 'bold', color: '#000' }}>Quantity</th>
                                                <th style={{ padding: '8px', fontSize: '14px', fontWeight: 'bold', color: '#000' }}>Total</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {order.items.map((item) => {
                                                const totalPrice = parseFloat(item.price) * item.quantity
                                                return (
                                                    <tr key={item.id} style={{ borderBottom: '1px solid #ddd' }}>
                                                        <td style={{ padding: '8px', fontSize: '14px', color: '#000' }}>{item.name}</td>
                                                        <td style={{ padding: '8px', fontSize: '14px', color: '#000' }}>
                                                            {(order.currencySymbol || currencySymbol)}{parseFloat(item.price).toFixed(2)}
                                                        </td>
                                                        <td style={{ padding: '8px', fontSize: '14px', color: '#000' }}>{item.quantity} ({item.portion})</td>
                                                        <td style={{ padding: '8px', fontSize: '14px', color: '#000' }}>  {(order.currencySymbol || currencySymbol)}{totalPrice.toFixed(2)}
                                                        </td>
                                                    </tr>
                                                )
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            <div
                                style={{
                                    background: '#fff',
                                    padding: '15px',
                                    borderRadius: '10px',
                                    marginBottom: '20px',
                                    boxShadow: '0 3px 8px rgba(0, 0, 0, 0.1)',
                                }}
                            >
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <h4 style={{
                                        textAlign: 'left', marginLeft: 10, marginTop: 5, fontWeight: 'bold', fontSize: '15px'
                                    }}>
                                        Bill Summary
                                    </h4>
                                    <small style={{ marginLeft: 10, fontFamily: "Poppins", fontSize: "11px", marginTop: 10 }}>
                                        {dayjs().format('dddd, DD MMM YYYY')}
                                    </small>
                                </div>
                                <div>
                                    <div className="row-center-space-between" style={{ marginTop: 10 }}>
                                        <span style={{ fontSize: '13px', fontFamily: 'Poppins', paddingInline: 10 }}>
                                            Sub Total Bill
                                        </span>
                                        <div style={{ marginRight: 10, display: 'flex', gap: 20, fontFamily: "Poppins", fontSize: '14px' }}>
                                            <span> {order.currencySymbol || currencySymbol} {order.subTotal.toFixed(2)}</span>
                                        </div>
                                    </div>

                                    {order.taxName && order.taxValue > 0 && (
                                        <div className="row-center-space-between" style={{ marginTop: 10 }}>
                                            <span style={{ fontSize: '13px', fontFamily: 'Poppins', paddingInline: 10 }}>
                                                {order.taxName} ({order.taxValue}%)
                                            </span>
                                            <div style={{ marginRight: 10, display: 'flex', gap: 20, fontFamily: "Poppins", fontSize: '14px' }}>
                                                <span> {order.currencySymbol || currencySymbol} {((order.taxValue / 100) * order.subTotal).toFixed(2)}</span>
                                            </div>
                                        </div>
                                    )}

                                    <div className="row-center-space-between" style={{ marginTop: 10 }}>
                                        <span style={{ fontSize: '13px', fontFamily: 'Poppins', paddingInline: 10, fontWeight: 'bold' }}>
                                            Grand Total
                                        </span>
                                        <div style={{ marginRight: 10, fontWeight: 'bold', display: 'flex', gap: 20, fontFamily: "Poppins", fontSize: '14px' }}>
                                            <span> {order.currencySymbol || currencySymbol} {order.grandTotal.toFixed(2)}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div style={{ textAlign: 'center' }}>
                                <components.Button
                                    text="Add More Dishes"
                                    colorScheme="primary"
                                    onClick={handleBack}
                                />
                            </div>
                        </>
                    ) : (
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                height: '60vh',
                                textAlign: 'center',
                            }}
                        >
                            <p style={{ fontSize: '14px', color: '#555' }}>
                                No order found for this table.
                            </p>
                        </div>
                    )}

                </div>
            </main>
        )
    }

    return (
        <>
            {renderHeader()}
            {renderContent()}
        </>
    )
}
